import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { toCategoryScope } from '@/components/blog/category-scope';
import PremiumGate, { type PremiumGateProps } from '@/components/premium/PremiumGate';
import type { SidebarConfig } from '@/config/sidebarConfig';
import { type Article, deleteArticleApi, getArticleCategoriesApi, getArticlesApi } from '@/lib/api/blogApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { updateSearchParams } from '@/lib/url-params';
import { useAuthStore } from '@/stores/auth-store';
import { formatDateAbbreviatedFr } from '@shared';
import { ArticleStatus } from '@shared/enums/domain.enum';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, Edit, Eye, FileText, ImageIcon, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../layout/DashboardLayout';
import PageHeader from '../ui/PageHeader';
import QueryErrorState from '../ui/QueryErrorState';

function getCoverUrl(files?: Array<{ id: number; key: string; usage: string }>): string | null {
  const cover = files?.find(f => f.usage === 'ARTICLE_COVER');
  if (!cover) return null;
  const imagesUrl = import.meta.env.VITE_IMAGES_URL as string | undefined;
  if (imagesUrl) return `${imagesUrl}/${cover.key.replace('./uploads/', '')}`;
  const apiUrl = import.meta.env.VITE_URL_API as string;
  return `${apiUrl}/v1/files/${cover.id}/serve`;
}

interface ScopedBlogListProps {
  sidebarConfig: SidebarConfig;
  createRoute: string;
  editRouteBase: string; // e.g. '/organizer/blog' → edit = '/organizer/blog/:id/edit'
  /** Extra query params to scope articles (e.g. { organizationId: 5 }) */
  scopeParams?: Record<string, number>;
  subtitle?: string;
  /** Show the author column (admin view only) */
  showAuthor?: boolean;
  /** Plan gate applied to the page content only: the sidebar stays usable. */
  gate?: Omit<PremiumGateProps, 'children'>;
}

export default function ScopedBlogList({
  sidebarConfig,
  createRoute,
  editRouteBase,
  scopeParams,
  subtitle = 'Gestion des articles',
  showAuthor = false,
  gate,
}: ScopedBlogListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const queryClient = useQueryClient();

  const urlPage = Math.max(1, Number(searchParams.get('page') ?? '1'));
  const urlStatus = searchParams.get('status') ?? '';
  const urlCategory = searchParams.get('categoryId') ?? '';
  const pageIndex = urlPage - 1;
  const pagination: PaginationState = { pageIndex, pageSize: 20 };

  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(searchTimeout.current), []);
  const search = searchParams.get('search') ?? '';

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(prev => updateSearchParams(prev, updates), { replace: true });
  }

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateParams({ search: value.trim() || undefined, page: undefined });
    }, 350);
  }

  const queryParams = {
    page: urlPage,
    limit: pagination.pageSize,
    ...(search && { search }),
    ...(urlStatus && { status: urlStatus }),
    ...(urlCategory && { categoryId: Number(urlCategory) }),
    ...scopeParams,
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.blog.articles(queryParams),
    queryFn: () => getArticlesApi(queryParams),
    placeholderData: keepPreviousData,
    enabled: isAuthenticated,
  });

  // Lot A bis : le filtre ne doit lister que la taxonomie du blog affiche.
  // Sinon un club Pro verrait dans son filtre des categories BoulesConnect qui
  // ne peuvent, par construction, remonter aucun de ses articles.
  const categoryScope = toCategoryScope(scopeParams);

  const { data: categories } = useQuery({
    queryKey: queryKeys.blog.categories(categoryScope),
    queryFn: () => getArticleCategoriesApi(categoryScope),
    enabled: isAuthenticated,
  });

  const { mutate: deleteArticle } = useMutation({
    mutationFn: deleteArticleApi,
    onSuccess: () => {
      toast.success('Article supprime');
      void queryClient.invalidateQueries({ queryKey: queryKeys.blog.all() });
    },
    onError: () => toast.error('Erreur lors de la suppression'),
  });

  const handleDelete = useCallback(
    (id: number, title: string) => {
      if (!window.confirm(`Supprimer l'article "${title}" ?`)) return;
      deleteArticle(id);
    },
    [deleteArticle],
  );

  const columns = useMemo<ColumnDef<Article>[]>(
    () => [
      {
        id: 'article',
        header: 'Article',
        cell: ({ row }) => {
          const a = row.original;
          const coverUrl = getCoverUrl(a.files);
          return (
            <div className='flex items-center gap-3'>
              {coverUrl ? (
                <img src={coverUrl} alt={a.title} className='h-10 w-14 flex-shrink-0 rounded-lg object-cover' />
              ) : (
                <div className='flex h-10 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600'>
                  <ImageIcon className='h-5 w-5 text-white' />
                </div>
              )}
              <div>
                <p className='font-semibold text-slate-900'>{a.title}</p>
                <p className='text-xs text-slate-500'>/{a.slug}</p>
              </div>
            </div>
          );
        },
      },
      {
        id: 'category',
        header: 'Categorie',
        cell: ({ row }) => {
          const cat = row.original.category;
          if (!cat) return <span className='text-slate-400'>—</span>;
          return (
            <span className='inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-slate-100 text-slate-700'>
              {cat.color && (
                <span className='inline-block h-2.5 w-2.5 rounded-full' style={{ backgroundColor: cat.color }} />
              )}
              {cat.name}
            </span>
          );
        },
      },
      ...(showAuthor
        ? [
            {
              id: 'author',
              header: 'Auteur',
              cell: ({ row }: { row: { original: Article } }) => {
                const author = row.original.author;
                if (!author) return '—';
                return (
                  <p className='text-sm text-slate-900'>
                    {author.firstName} {author.lastName}
                  </p>
                );
              },
            },
          ]
        : []),
      {
        id: 'status',
        header: 'Statut',
        cell: ({ row }) => {
          const s = row.original.status;
          const isPublished = s === ArticleStatus.PUBLISHED;
          return (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
                isPublished ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
              }`}
            >
              {isPublished ? <Eye className='h-3 w-3' /> : <FileText className='h-3 w-3' />}
              {isPublished ? 'Publie' : 'Brouillon'}
            </span>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        cell: ({ row }) => <p className='text-sm text-slate-900'>{formatDateAbbreviatedFr(row.original.createdAt)}</p>,
      },
      {
        id: 'actions',
        header: () => <span className='sr-only'>Actions</span>,
        cell: ({ row }) => (
          <div className='flex items-center justify-end gap-2'>
            <Link
              to={`${editRouteBase}/${row.original.id}/edit${scopeParams?.organizationId ? `?organizationId=${scopeParams.organizationId}` : ''}`}
              className='rounded-lg p-2 transition hover:bg-slate-100'
              title='Modifier'
            >
              <Edit className='h-4 w-4 text-slate-600' />
            </Link>
            <button
              onClick={() => handleDelete(row.original.id, row.original.title)}
              className='rounded-lg p-2 transition hover:bg-red-50'
              title='Supprimer'
            >
              <Trash2 className='h-4 w-4 text-red-600' />
            </button>
          </div>
        ),
      },
    ],
    [editRouteBase, showAuthor, scopeParams, handleDelete],
  );

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    pageCount: data?.meta?.totalPages ?? -1,
    state: { pagination },
    onPaginationChange: updater => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      updateParams({ page: String(next.pageIndex + 1) });
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const total = data?.meta?.total ?? 0;
  const from = total === 0 ? 0 : pageIndex * pagination.pageSize + 1;
  const to = Math.min((pageIndex + 1) * pagination.pageSize, total);

  const isPageLoading = isLoading || !isAuthenticated;

  const content = (
    <div className='p-8'>
      <div className='mb-6 flex items-center justify-between'>
        <PageHeader title='Blog' subtitle={subtitle} />
        <Link
          to={createRoute}
          className='inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700'
        >
          <Plus className='h-4 w-4' />
          Nouvel article
        </Link>
      </div>

      {/* Filters */}
      <div className='mb-6 rounded-xl border border-slate-200 bg-white p-6'>
        <div className='flex flex-wrap items-center gap-4'>
          <div className='relative max-w-md flex-1'>
            <Search className='absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400' />
            <input
              type='text'
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder='Rechercher un article...'
              className='w-full rounded-lg border border-slate-200 py-2.5 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none'
            />
          </div>

          <select
            value={urlStatus}
            onChange={e => updateParams({ status: e.target.value || undefined, page: undefined })}
            className='rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none'
          >
            <option value=''>Tous les statuts</option>
            <option value={ArticleStatus.DRAFT}>Brouillon</option>
            <option value={ArticleStatus.PUBLISHED}>Publie</option>
          </select>

          <select
            value={urlCategory}
            onChange={e => updateParams({ categoryId: e.target.value || undefined, page: undefined })}
            className='rounded-lg border border-slate-200 px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none'
          >
            <option value=''>Toutes les categories</option>
            {(categories ?? []).map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      {isPageLoading ? (
        <div className='flex justify-center py-20'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent' />
        </div>
      ) : isError ? (
        <QueryErrorState message='Impossible de charger les articles' onRetry={() => void refetch()} />
      ) : (
        <>
          <div className='overflow-hidden rounded-xl border border-slate-200 bg-white'>
            <table className='w-full'>
              <thead className='border-b bg-slate-50'>
                {table.getHeaderGroups().map(hg => (
                  <tr key={hg.id}>
                    {hg.headers.map(header => (
                      <th
                        key={header.id}
                        className='px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600'
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className='divide-y divide-slate-100'>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className='px-6 py-12 text-center text-slate-500'>
                      Aucun article trouve
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <tr key={row.id} className='transition hover:bg-slate-50'>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className='px-6 py-4'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className='mt-4 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-6 py-3'>
              <p className='text-sm text-slate-600'>
                {from}–{to} sur {total}
              </p>
              <div className='flex items-center gap-1'>
                <button
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  className='rounded-lg p-2 transition hover:bg-slate-100 disabled:opacity-30'
                >
                  <ChevronLeft className='h-4 w-4' />
                </button>
                <button
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  className='rounded-lg p-2 transition hover:bg-slate-100 disabled:opacity-30'
                >
                  <ChevronRight className='h-4 w-4' />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <DashboardLayout sidebarConfig={sidebarConfig}>
      {gate ? <PremiumGate {...gate}>{content}</PremiumGate> : content}
    </DashboardLayout>
  );
}
